import { registerBlockType } from "@wordpress/blocks"
import { __ } from "@wordpress/i18n"
import { useSelect } from "@wordpress/data"
import {
  useBlockProps,
  RichText,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import {
  PanelBody,
  SelectControl,
  TextControl,
  CheckboxControl,
  Button,
  Spinner
} from "@wordpress/components"
import { useState, useMemo } from "@wordpress/element"

const Edit = ({ attributes, setAttributes }) => {
  const blockProps = useBlockProps()
  const { sectionTitle, successStories, featuredImage } = attributes
  const [searchTerm, setSearchTerm] = useState("")

  // Fetch all Success Story posts
  const successStoryPosts = useSelect((select) => {
    return select("core").getEntityRecords("postType", "success_stories", {
      per_page: -1,
      _fields: ["id", "title"],
      status: "publish",
    })
  }, [])

  const isLoading = successStoryPosts === null

  // Create options for checkbox selection
  const successStoryOptions = successStoryPosts
    ? successStoryPosts.map((post) => ({
        value: post.id.toString(),
        label: post.title.rendered,
      }))
    : []

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return successStoryOptions
    return successStoryOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q)
    )
  }, [successStoryOptions, searchTerm])

  const selected = successStories || []
  const maxSelect = 3

  // Handle selection toggle
  const toggleSelect = (value) => {
    const has = selected.includes(value)
    if (has) {
      setAttributes({ successStories: selected.filter((v) => v !== value) })
    } else if (selected.length < maxSelect) {
      setAttributes({ successStories: [...selected, value] })
    }
  }

  return (
    <div {...blockProps}>
      <InspectorControls>
        <PanelBody title={__("Settings", "puroclean")}>
          <TextControl
            label={__("Section Title", "puroclean")}
            value={sectionTitle}
            onChange={(value) => setAttributes({ sectionTitle: value })}
          />
          
          {/* Featured Image Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Featured Image:', 'puroclean')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  featuredImage: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Construction and Restoration Work'
                  }
                })}
                allowedTypes={['image']}
                value={featuredImage.id}
                render={({ open }) => (
                  <div>
                    {featuredImage.url ? (
                      <div>
                        <img 
                          src={featuredImage.url} 
                          alt={featuredImage.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            featuredImage: { url: '', id: 0, alt: 'Construction and Restoration Work' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Image', 'puroclean')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Featured Image', 'puroclean')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>

          {/* Success Stories Selection */}
          <div style={{ marginTop: 12 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
              {__("Select Success Stories", "puroclean")} ({selected.length}/{maxSelect})
            </label>

            <TextControl
              placeholder={__("Search…", "puroclean")}
              value={searchTerm}
              onChange={setSearchTerm}
            />

            {isLoading && <Spinner />}

            {!isLoading && (
              <div
                style={{
                  maxHeight: 240,
                  overflow: "auto",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  padding: 8,
                  marginTop: 8,
                }}
              >
                {filteredOptions.length === 0 && (
                  <p style={{ margin: 0, color: "#666" }}>
                    {__("No results", "puroclean")}
                  </p>
                )}

                {filteredOptions.map((opt) => (
                  <CheckboxControl
                    key={opt.value}
                    label={opt.label}
                    checked={selected.includes(opt.value)}
                    onChange={() => toggleSelect(opt.value)}
                    disabled={!selected.includes(opt.value) && selected.length >= maxSelect}
                  />
                ))}
              </div>
            )}

            <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
              {__("Select up to 3 success stories to display", "puroclean")}
            </p>
          </div>
        </PanelBody>
      </InspectorControls>

      <div className="py-4 md:py-8 lg:py-16 bg-gray-50">
        <div className="max-w-inner mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-12 items-start">
            {/* Left Column - Image */}
            <div className="lg:col-span-6">
              {featuredImage.url ? (
                <img 
                  src={featuredImage.url}
                  alt={featuredImage.alt} 
                  className="h-[244px] rounded-lg w-full lg:h-full object-cover"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div className="h-[244px] rounded-lg w-full lg:h-full bg-gray-200 flex items-center justify-center">
                  <p className="text-gray-500">No image selected</p>
                </div>
              )}
            </div>
            
            {/* Right Column - Content */}
            <div className="lg:col-span-5">
              <div className="flex flex-col">
                <RichText
                  tagName="h2"
                  className="text-primary text-xl font-bold mb-2 lg:mb-6 leading-tight"
                  value={sectionTitle}
                  onChange={(value) => setAttributes({ sectionTitle: value })}
                />
                
                <div className="flex-1 flex flex-col justify-evenly">
                  {successStoryPosts && selected.map((id) => {
                    const post = successStoryPosts.find((p) => p.id.toString() === id)
                    if (!post) return null
                    return (
                      <div key={id} className="flex items-start py-2 lg:py-4">
                        <p className="flex items-start font-bold mb-0 leading-tight">
                          <img 
                            src="/wp-content/themes/puroclean-tw/assets/img/linking-arrow-black.svg"
                            alt="Linking Arrow" 
                            className="mr-3 mt-1 flex-shrink-0 w-[1.375rem] sm:w-[1.75rem] h-auto"
                          />
                          <span className="text-3xl">
                            {post.title.rendered}
                          </span>
                        </p>
                      </div>
                    )
                  })}
                  {selected.length === 0 && (
                    <p className="text-gray-500 italic">No success stories selected</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

registerBlockType("puroclean/success-stories", {
  edit: Edit,
  save: () => null // Server-side rendered block
}) 